// ============================================================
// PROJECT 1 THEME - Exact styling from working version
// ============================================================

// ── Light / Dark mode ───────────────────────────────────────
// The actual colors live in index.css as CSS variables. These
// helpers just flip the data-theme attribute on <html> and
// remember the choice. Components don't need to re-render — the
// var(--token) values cascade automatically.
export function getThemeMode() {
  try { return localStorage.getItem('theme') === 'dark' ? 'dark' : 'light'; }
  catch { return 'light'; }
}
export function applyThemeMode(mode) {
  const m = mode === 'dark' ? 'dark' : 'light';
  if (typeof document !== 'undefined') document.documentElement.setAttribute('data-theme', m);
  try { localStorage.setItem('theme', m); } catch {}
  return m;
}
export function initThemeMode() { return applyThemeMode(getThemeMode()); }
export function toggleThemeMode() { return applyThemeMode(getThemeMode() === 'light' ? 'dark' : 'light'); }

export const THEME = {
  // Backgrounds
  bgMain: 'var(--bg)',
  bgCard: 'var(--surface)',
  bgCardAlt: 'var(--bg)',  // Alternate card background
  bgInput: 'var(--bg)',
  bgHover: 'var(--surface-2)',
  bgGradient: 'linear-gradient(135deg,var(--bg),var(--surface),var(--surface-2))',
  
  // Borders
  borderPrimary: 'var(--border)',
  borderSecondary: 'var(--surface-2)',
  
  // Text
  textPrimary: 'var(--text)',
  textSecondary: 'var(--text)',
  textTertiary: 'var(--text-2)',
  textMuted: 'var(--text-3)',
  textDim: 'var(--text-4)',
  textLabel: 'var(--surface-3)',
  textWhite: 'var(--text-strong)',
  
  // Accents
  primary: 'var(--link)',      // Light blue
  primaryMid: '#3b82f6',   // Medium blue
  primaryDark: 'var(--border)',  // Dark blue
  success: '#22c55e',
  warning: '#f59e0b',
  warningLight: '#fbbf24',
  error: '#ef4444',
  errorDark: '#7f1d1d',
  errorBg: '#7f1d1d22',
  errorText: '#fca5a5',
  purple: '#c084fc',
  
  // Fonts
  fontMono: "'DM Mono',monospace",
  fontSerif: "'Cormorant Garamond',serif",
};

// Card component matching Project 1
export const Card = ({children, style, alt}) => (
  <div style={{
    background: alt ? THEME.bgCardAlt : THEME.bgCard,
    border: `1px solid ${alt ? THEME.borderSecondary : THEME.borderPrimary}`,
    borderRadius: '10px',
    padding: '12px',
    ...style
  }}>
    {children}
  </div>
);

// Section title matching Project 1 stats panels
export const SectionTitle = ({children, style}) => (
  <div style={{
    color: THEME.primaryMid,
    fontSize: '8px',
    fontWeight: 700,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    marginBottom: '8px',
    ...style
  }}>
    {children}
  </div>
);

// Page title matching Project 1 header
export const PageTitle = ({children, subtitle, style}) => (
  <div style={{marginBottom: '16px', ...style}}>
    <div style={{
      fontFamily: THEME.fontSerif,
      fontSize: '22px',
      color: THEME.textWhite,
      letterSpacing: '-0.5px',
      marginBottom: subtitle ? '4px' : 0
    }}>
      {children}
    </div>
    {subtitle && (
      <div style={{
        color: THEME.textLabel,
        fontSize: '9px',
        letterSpacing: '1.5px',
        textTransform: 'uppercase'
      }}>
        {subtitle}
      </div>
    )}
  </div>
);

// Stat card matching Project 1
export const StatCard = ({label, value, color, style}) => (
  <div style={{textAlign: 'center', ...style}}>
    <div style={{
      fontFamily: THEME.fontSerif,
      fontSize: '18px',
      fontWeight: 700,
      color: color || THEME.primary
    }}>
      {value}
    </div>
    <div style={{
      fontSize: '8px',
      color: THEME.textLabel,
      letterSpacing: '1.5px',
      marginTop: '2px'
    }}>
      {label}
    </div>
  </div>
);

// Button styles matching Project 1
export const Button = ({children, onClick, variant = 'primary', style}) => {
  const variants = {
    primary: {
      padding: '5px 13px',
      borderRadius: '7px',
      border: `1px solid ${THEME.primaryDark}`,
      background: THEME.primaryDark,
      color: THEME.primary,
      cursor: 'pointer',
      fontSize: '10px'
    },
    secondary: {
      padding: '5px 13px',
      borderRadius: '7px',
      border: `1px solid ${THEME.borderPrimary}`,
      background: 'transparent',
      color: THEME.primary,
      cursor: 'pointer',
      fontSize: '10px'
    },
    success: {
      padding: '5px 13px',
      borderRadius: '7px',
      border: `1px solid ${THEME.borderPrimary}`,
      background: 'transparent',
      color: THEME.success,
      cursor: 'pointer',
      fontSize: '10px'
    },
    danger: {
      padding: '4px 8px',
      borderRadius: '5px',
      border: `1px solid ${THEME.errorDark}`,
      background: THEME.errorBg,
      color: THEME.errorText,
      cursor: 'pointer',
      fontSize: '9px'
    }
  };
  
  return (
    <button onClick={onClick} style={{...variants[variant], ...style}}>
      {children}
    </button>
  );
};

// Header matching Project 1
export const Header = ({children, stats}) => (
  <div style={{
    background: THEME.bgGradient,
    borderBottom: `1px solid ${THEME.borderPrimary}`,
    padding: '14px 24px',
    position: 'sticky',
    top: 0,
    zIndex: 50
  }}>
    <div style={{
      maxWidth: '1240px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '10px'
    }}>
      {children}
    </div>
    {stats && (
      <div style={{maxWidth: '1240px', margin: '7px auto 0'}}>
        {stats}
      </div>
    )}
  </div>
);

// Main container matching Project 1
export const Container = ({children, style}) => (
  <div style={{
    fontFamily: THEME.fontMono,
    background: THEME.bgMain,
    minHeight: '100vh',
    color: THEME.textPrimary,
    ...style
  }}>
    {children}
  </div>
);

// Content area matching Project 1
export const Content = ({children, style}) => (
  <div style={{
    maxWidth: '1240px',
    margin: '0 auto',
    padding: '20px 24px',
    ...style
  }}>
    {children}
  </div>
);

// Input field matching Project 1
export const Input = ({value, onChange, placeholder, style, ...props}) => (
  <input
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    style={{
      padding: '7px 10px',
      borderRadius: '7px',
      border: `1px solid ${THEME.borderPrimary}`,
      background: THEME.bgInput,
      color: THEME.textPrimary,
      fontSize: '11px',
      outline: 'none',
      width: '100%',
      boxSizing: 'border-box',
      ...style
    }}
    {...props}
  />
);

// Select dropdown matching Project 1
export const Select = ({value, onChange, children, style, ...props}) => (
  <select
    value={value}
    onChange={onChange}
    style={{
      padding: '7px 10px',
      borderRadius: '7px',
      border: `1px solid ${THEME.borderPrimary}`,
      background: THEME.bgInput,
      color: THEME.textPrimary,
      fontSize: '11px',
      width: '100%',
      boxSizing: 'border-box',
      cursor: 'pointer',
      ...style
    }}
    {...props}
  >
    {children}
  </select>
);
